/**
 * Shared risk, confidence, and color configuration.
 * Single source of truth for the Viridis palette and risk scoring UI.
 */

/**
 * Risk score configuration (Viridis palette - matches map legend)
 * Score range: -2 (Very High risk) to 2 (Very Low risk)
 *
 * The `label`s are mirrored by VERDICT_BANDS in packages/api/verdict_bands.py,
 * which records the band a lookup was shown as (behaviour-logging TR §3.1). A
 * hand-checked pair, not generated: if a label changes here, change it there
 * and diff the two by hand (task 6.22) — the events endpoint rejects a
 * `verdict_band` that is not one of the server's labels.
 */
export const RISK_CONFIG = {
  "-2": {
    label: "Very High",
    shortLabel: "V.High",
    hex: "#440154",
    hexDark: "#a78bfa",
    ring: "ring-purple-900/30",
    textColor: "white",
    value: 95,
  },
  "-1": {
    label: "High",
    shortLabel: "High",
    hex: "#3b528b",
    hexDark: "#818cf8",
    ring: "ring-blue-800/30",
    textColor: "white",
    value: 75,
  },
  0: {
    label: "Moderate",
    shortLabel: "Mod",
    hex: "#21918c",
    hexDark: "#2dd4bf",
    ring: "ring-teal-600/30",
    textColor: "white",
    value: 50,
  },
  1: {
    label: "Low",
    shortLabel: "Low",
    hex: "#5ec962",
    hexDark: "#5ec962",
    ring: "ring-green-500/30",
    textColor: "black",
    value: 25,
  },
  2: {
    label: "Very Low",
    shortLabel: "V.Low",
    hex: "#fde725",
    hexDark: "#fde725",
    ring: "ring-yellow-400/30",
    textColor: "black",
    value: 10,
  },
};

/**
 * Score-type-aware risk descriptions.
 *
 * "Detected" language is only ever true for measured data; interpolated and
 * estimated scores are modeled values and must never read as lab results or
 * issue directive health guidance. Descriptions previously lived on
 * RISK_CONFIG and said "contamination detected. Immediate action recommended"
 * for modeled cells — a factual and liability failure (remediation plan,
 * Finding 9). Always go through getRiskDescription / getRecommendation.
 */
const RISK_DESCRIPTIONS = {
  "-2": {
    measured:
      "Significant contamination detected in tests from this area. Verification with a certified water test is strongly recommended.",
    modeled:
      "Modeling suggests potentially significant contamination near this area. No direct measurement exists here — verify with a certified water test before acting.",
  },
  "-1": {
    measured:
      "Elevated contaminant levels detected in tests from this area. Testing and filtration are worth considering.",
    modeled:
      "Modeling suggests elevated contaminant levels may occur near this area. Verify with a certified water test.",
  },
  0: {
    measured:
      "Some contaminants detected in tests from this area. Consider additional testing.",
    modeled:
      "Modeling suggests moderate contaminant levels are possible near this area. A certified water test would confirm.",
  },
  1: {
    measured:
      "Minimal contamination detected in tests from this area. Water quality is generally good.",
    modeled:
      "Modeling suggests low contaminant levels near this area, based on nearby measurements.",
  },
  2: {
    measured:
      "Negligible contamination detected in tests from this area. Excellent water quality.",
    modeled:
      "Modeling suggests very low contaminant levels near this area, based on nearby measurements.",
  },
};

const RECOMMENDATIONS = {
  "-2": {
    measured:
      "Consider alternative water sources and arrange professional testing. Contact your water utility for guidance.",
    modeled:
      "This is a modeled estimate, not a measurement. Arrange a certified water test to establish actual conditions before making decisions.",
  },
  "-1": {
    measured:
      "Schedule water testing soon. Consider installing filtration appropriate to the contaminants found.",
    modeled:
      "This is a modeled estimate. A certified water test is the right next step to confirm local conditions.",
  },
  0: {
    measured: "Periodic testing recommended. Standard filtration may be beneficial.",
    modeled:
      "This is a modeled estimate. Periodic testing would establish actual conditions at this location.",
  },
  1: {
    measured: "Annual testing suggested. Measured water quality appears acceptable.",
    modeled:
      "This is a modeled estimate based on nearby measurements. Annual testing would confirm.",
  },
  2: {
    measured:
      "Measured water quality is excellent. Regular monitoring is still recommended.",
    modeled:
      "This is a modeled estimate based on nearby measurements. Modeled quality looks excellent; a test would confirm.",
  },
};

export const isMeasured = (scoreType) => scoreType === "measured";

export const getRiskDescription = (score, scoreType) => {
  const entry = RISK_DESCRIPTIONS[score] || RISK_DESCRIPTIONS[0];
  return isMeasured(scoreType) ? entry.measured : entry.modeled;
};

export const getRecommendation = (score, scoreType) => {
  const entry = RECOMMENDATIONS[score] || RECOMMENDATIONS[0];
  return isMeasured(scoreType) ? entry.measured : entry.modeled;
};

/** Human label for a score_type value — never conflates modeled with measured. */
export const getScoreTypeLabel = (scoreType) => {
  if (scoreType === "measured") return "Measured";
  if (scoreType === "interpolated") return "Modeled (interpolated)";
  if (scoreType === "estimated") return "Modeled (estimated)";
  return "—";
};

export const RISK_ORDER = ["-2", "-1", "0", "1", "2"];

/**
 * The order scales and legends are *drawn* in: least severe on the left,
 * most severe on the right, so the ramp climbs the way a reader expects a
 * measure of "how bad" to climb.
 *
 * Separate from RISK_ORDER, which stays worst-first because it drives
 * iteration elsewhere. This one is presentational, and everything that paints
 * the ramp reads it, so a legend and a scale can never point opposite ways.
 *
 * (This reverses the left-to-right direction design review W2.1 / W2.2 chose.
 * Very High on the left tested as backwards for readers who expect a scale to
 * ascend.)
 */
export const RISK_SCALE_ORDER = [...RISK_ORDER].reverse();

/** Viridis hex ramp in drawing order, derived from RISK_CONFIG so the legends
 *  can't drift from the map fills. */
export const VIRIDIS_RAMP = RISK_SCALE_ORDER.map((score) => RISK_CONFIG[score].hex);

/** Full-ramp CSS gradient (Very Low on the left) shared by every legend. */
export const VIRIDIS_GRADIENT = `linear-gradient(to right, ${VIRIDIS_RAMP.join(", ")})`;

/**
 * Confidence score configuration
 */
export const CONFIDENCE_CONFIG = {
  high: {
    label: "High",
    value: 95,
    color: "text-emerald-600 dark:text-emerald-400",
    bgColor: "bg-emerald-500",
    description: "Based on direct water testing at this location or nearby sites.",
  },
  moderate: {
    label: "Moderate",
    value: 75,
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-500",
    description: "Based on regional data and statistical modeling.",
  },
  low: {
    label: "Low",
    value: 45,
    color: "text-gray-600 dark:text-gray-400",
    bgColor: "bg-gray-500",
    description: "Limited data available. Estimate based on broader regional trends.",
  },
};

/**
 * Contaminant category configuration
 */
export const CONTAMINANT_CATEGORIES = {
  METAL: { label: "Heavy Metals", color: "text-slate-600 dark:text-slate-400" },
  CHEMICAL: { label: "Chemicals", color: "text-purple-600 dark:text-purple-400" },
  ORGANIC: { label: "Organic Compounds", color: "text-green-600 dark:text-green-400" },
  MICROBIAL: { label: "Microbial", color: "text-red-600 dark:text-red-400" },
};

/**
 * Get confidence level from score type and test count.
 * Used when you have scoreType ("measured"/"estimated") and testCount.
 */
export const getConfidenceLevelFromType = (scoreType, testCount) => {
  if (scoreType === "measured" && testCount > 5) return "high";
  if (scoreType === "measured") return "moderate";
  return "low";
};

/**
 * Get confidence level from a numeric confidence score.
 * Used when you have a 0-100 confidence percentage.
 */
export const getConfidenceLevelFromScore = (confidenceScore) => {
  if (confidenceScore == null) return "low";
  if (confidenceScore >= 80) return "high";
  if (confidenceScore >= 50) return "moderate";
  return "low";
};

/**
 * The same three levels, said as evidence rather than as a percentage.
 *
 * `confidence_score` is not a statistical confidence: the scoring pipeline
 * derives it from how much data exists — test count, tests per site, the span
 * the tests cover (packages/scoring/features/aggregator.py). Printing it as
 * "82%" claims a precision it does not have, and re-states figures that are
 * already on screen beside it. A band says the same thing without the false
 * decimal point.
 *
 * Deliberately reuses getConfidenceLevelFromScore's cut points rather than
 * inventing a second set, so this can never disagree with the confidence level
 * the portfolio and the PDF template already show.
 */
export const EVIDENCE_BANDS = {
  high: "Well evidenced",
  moderate: "Adequately evidenced",
  low: "Limited evidence",
};

export const getEvidenceBand = (confidenceScore) =>
  EVIDENCE_BANDS[getConfidenceLevelFromScore(confidenceScore)];

/**
 * The same three bands as a bare degree, for a label-value register where the
 * label already supplies the noun: "EVIDENCE / Well evidenced" says it twice.
 *
 * A second *rendering*, not a second classification — both of these read
 * `getConfidenceLevelFromScore`, so the thresholds that decide which band an
 * address falls in stay in one place and cannot drift between the two.
 */
export const EVIDENCE_DEGREES = {
  high: "Strong",
  moderate: "Adequate",
  low: "Limited",
};

export const getEvidenceDegree = (confidenceScore) =>
  EVIDENCE_DEGREES[getConfidenceLevelFromScore(confidenceScore)];

/**
 * Viridis gradient for risk score progress bars.
 * Builds a CSS gradient from Very Low (yellow) through to the given score level.
 */
/**
 * Get the appropriate risk hex color for the current theme.
 * @param {string|number} score - Risk score key
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {string} Hex color string
 */
export const getRiskHex = (score, isDark = false) => {
  const config = RISK_CONFIG[score] || RISK_CONFIG["0"];
  return isDark ? config.hexDark : config.hex;
};

/** Neutral fill for map polygons/choropleth cells with no score. */
export const RISK_NODATA_HEX = "#cccccc";

/**
 * Fill hex for a risk score, returning a neutral gray for null/unknown scores.
 * Drop-in replacement for the legacy mapUtils `colorScale` (design review W1.2).
 */
export const getRiskFillHex = (score, isDark = false) => {
  const config = RISK_CONFIG[score];
  if (!config) return RISK_NODATA_HEX;
  return isDark ? config.hexDark : config.hex;
};

/**
 * Canonical human label for a risk score — "Very Low", never the legacy
 * "Very Low to None", and no phantom "-3 → Estimated" bucket (design review W4.2).
 */
export const getRiskLabel = (score) => RISK_CONFIG[score]?.label ?? "No data";

/**
 * The band label for a -2..2 score, or null for no score or an off-scale one —
 * what the event log records (`verdict_band`), so never "No data". Mirrors
 * verdict_band_for in packages/api/verdict_bands.py: 0.5 is not a band.
 */
export const verdictBandFor = (score) =>
  Number.isInteger(score) ? (RISK_CONFIG[score]?.label ?? null) : null;

/**
 * Text color (a CSS color keyword) that meets contrast on the score's fill.
 * Fixes the legacy `fontColorScale` bug that painted white text on the
 * light-green "Low" fill (design review W1.2 / §2.2).
 */
export const getRiskTextColor = (score) => RISK_CONFIG[score]?.textColor || "white";

export const getViridisGradient = (score) => {
  const colors = {
    2: "#fde725",
    1: "#5ec962",
    0: "#21918c",
    "-1": "#3b528b",
    "-2": "#440154",
  };

  const scoreOrder = ["2", "1", "0", "-1", "-2"];
  const scoreIndex = scoreOrder.indexOf(String(score));

  if (scoreIndex === -1) return colors["0"];

  const activeColors = scoreOrder.slice(0, scoreIndex + 1).map((s) => colors[s]);

  if (activeColors.length === 1) {
    return activeColors[0];
  }

  const stops = activeColors.map((color, i) => {
    const percent = (i / (activeColors.length - 1)) * 100;
    return `${color} ${percent}%`;
  });

  return `linear-gradient(to right, ${stops.join(", ")})`;
};
